'use client'

import {
  Autocomplete,
  type AutocompleteProps,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material'

export interface StructuredOption {
  id: number
}

const isInvalidValue = <T extends StructuredOption>(
  value: T | T[] | null
): value is T[] | null => value === null || Array.isArray(value)

function ListSkeleton() {
  return (
    <Stack
      spacing={0.5}
      role="status"
      aria-live="polite"
      aria-label="Loading options"
    >
      <Skeleton variant="text" height={30} />
      <Skeleton variant="text" height={30} />
      <Skeleton variant="text" height={30} />
    </Stack>
  )
}

type BaseProps<T extends StructuredOption> = Omit<
  AutocompleteProps<T, boolean | undefined, undefined, undefined>,
  'value' | 'onChange' | 'renderInput' | 'error'
> & {
  getOptionLabel: (option: T) => string
  label?: string
  error?: boolean
  required?: boolean
}

type StructuredAutocompleteProps<T extends StructuredOption> =
  | (BaseProps<T> & {
      multiple?: false
      value: number | null
      onChange: (event: React.SyntheticEvent, value: number | null) => void
    })
  | (BaseProps<T> & {
      multiple: true
      value: number[]
      onChange: (event: React.SyntheticEvent, value: number[]) => void
    })

export function StructuredAutocomplete<T extends StructuredOption>({
  options,
  value,
  onChange,
  getOptionLabel: getLabel,
  label,
  multiple,
  error,
  required,
  ...props
}: StructuredAutocompleteProps<T>): React.ReactElement {
  const getValueOption = (id: number) => options.find((opt) => opt.id === id)

  let optionValue: T | T[] | null
  if (multiple && Array.isArray(value)) {
    optionValue = value
      .map(getValueOption)
      .filter((opt): opt is T => opt !== undefined)
  } else if (!multiple && typeof value === 'number') {
    optionValue = getValueOption(value) ?? null
  } else {
    optionValue = null
  }

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: T | T[] | null
  ) => {
    if (multiple) {
      onChange(
        event,
        Array.isArray(newValue) ? newValue.map((opt) => opt.id) : []
      )
    } else {
      onChange(event, newValue && !Array.isArray(newValue) ? newValue.id : null)
    }
  }

  return (
    <Autocomplete
      {...props}
      multiple={multiple}
      options={options}
      value={optionValue}
      onChange={handleChange}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, val) =>
        isInvalidValue(val) || isInvalidValue(option)
          ? false
          : option.id === val.id
      }
      loadingText={props.loadingText ?? <ListSkeleton />}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          required={required}
        />
      )}
    />
  )
}
